import {
  JSXElementConstructor,
  MouseEvent,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { TableCellProps, WbTableComponents } from '../types';

export interface WbTableContextProviderProps<T> {
  tableSettings: any;
  children?: ReactNode;
  components: WbTableComponents<T>;
  selection?: 'single' | 'multiple';
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  checkIfDisabled?: (row: T) => boolean;
  tooltipIfDisabled?: string;
  treeProps?: {
    getRowChildren: (row: T) => T[];
    expandedRowsIds: string[];
    onExpandChange: (newExpandedIds: string[]) => void;
  };
  getRowId?: (row: T) => string;
  rowDynamicClassName?: (row: T) => string | undefined;
  onRowClick?: (
    row: T,
    event: MouseEvent<HTMLTableRowElement, MouseEvent>,
    index: number,
  ) => void;
}

export interface WbTableContextType<T> extends WbTableContextProviderProps<T> {
  tableSettings: any;
  columns: Array<TableCellProps<T>>;
  disableRowHighlightOnHover: boolean;
  rows: Array<T>;
  RowsWrapper: JSXElementConstructor<any>;
  selection?: 'single' | 'multiple';
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  checkIfDisabled?: (row: T) => boolean;
  tooltipIfDisabled?: string;
}

export const WbTableContext = createContext<WbTableContextType<any> | null>(
  null,
);

export const DefaultWrapper = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

export const WbTableContextProvider = <T,>({
  tableSettings,
  children,
  components,
  selectedItems,
  setSelectedItems,
  selection,
  checkIfDisabled,
  tooltipIfDisabled,
}: WbTableContextProviderProps<T>) => {
  const columns = components?.tableContent?.columns ?? [];
  const rows = components?.tableContent?.rows ?? [];
  const onRowClick = components.tableContent?.onRowClick;
  const rowDynamicClassName = components.tableContent?.rowDynamicClassName;
  const treeProps = components.tableContent?.treeProps;
  const getRowId = components.tableContent?.getRowId;
  const disableRowHighlightOnHover =
    components.tableContent?.disableRowHighlightOnHover ?? false;

  const RowsWrapper = components?.tableContent?.rowsWrapper || DefaultWrapper;

  useEffect(() => {
    if (selection === 'single') setSelectedItems?.([rows[0]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WbTableContext.Provider
      value={{
        tableSettings,
        components,
        columns,
        rows,
        selection,
        selectedItems,
        setSelectedItems,
        tooltipIfDisabled,
        checkIfDisabled,
        onRowClick,
        rowDynamicClassName,
        treeProps,
        getRowId,
        disableRowHighlightOnHover,
        RowsWrapper,
      }}
    >
      {children}
    </WbTableContext.Provider>
  );
};

export const useWbTableContext = <T,>() => {
  const context = useContext(WbTableContext);
  if (!context) {
    throw new Error(
      'useWbTableContext must be used within a WbTableContextProvider',
    );
  }
  return context as WbTableContextType<T>;
};
